import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmailVariableGroup } from './EmailBodyEditor/types';
import {
  buildTranslationOptions,
  buildVariableOptions,
  filterVariableOptions,
  findVariableTrigger,
  TranslationEntry,
  VariableOption,
  VariableTrigger,
} from './variableOptions';

interface UseVariableSuggestionsArgs {
  variableGroups?: EmailVariableGroup[];
  /**
   * The partner's saved texts, offered after the variables — see
   * `useTranslationEntries`. Omitted, the list is variables only.
   */
  translations?: TranslationEntry[];
  /**
   * Applies a pick. `trigger.start`…`trigger.end` is the half-typed `{{ cust`
   * the variable replaces, in whatever offsets the host fed to `sync` —
   * string indexes for a text field, document positions for an editor.
   */
  onAccept: (option: VariableOption, trigger: VariableTrigger) => void;
  /** False switches the list off entirely (read-only surfaces). */
  enabled?: boolean;
}

export interface VariableSuggestions {
  /** The whole catalog, for hosts that also render the picker button. */
  options: VariableOption[];
  /** What the typed query narrows it to. */
  suggestions: VariableOption[];
  open: boolean;
  activeIndex: number;
  trigger: VariableTrigger | null;
  /**
   * Feed the text before the caret after every edit and every caret move.
   * `baseOffset` is where that slice starts, for hosts that pass a window
   * rather than the whole text.
   */
  sync: (textBeforeCaret: string, baseOffset?: number) => void;
  /**
   * Offers the list the key first. True means it was the list's — the host must
   * swallow it, or Enter inserts a newline as well as a variable.
   */
  handleKey: (key: string) => boolean;
  accept: (option: VariableOption) => void;
  close: () => void;
}

/**
 * The `{{`-triggered autocomplete — variables and the partner's saved texts — minus
 * any opinion about where the text lives.
 *
 * Three surfaces need it — the subject/pretext fields, the Twig source editor and
 * the rich-text blocks — and they agree on nothing except the behaviour: `{{` opens
 * a list, typing narrows it, arrows move, Enter inserts, Escape dismisses. So the
 * behaviour is here, and each host supplies the two things only it can know: the
 * text before its caret, and how to replace a range in its own document.
 */
export function useVariableSuggestions({
  variableGroups,
  translations,
  onAccept,
  enabled = true,
}: UseVariableSuggestionsArgs): VariableSuggestions {
  const [trigger, setTrigger] = useState<VariableTrigger | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  /**
   * The trigger Escape dismissed, as `start:query`. Without it the next caret
   * report re-opens the list on the very same `{{`; keyed by the query rather
   * than by a flag, so typing another character brings it back.
   */
  const dismissedRef = useRef<string | null>(null);

  /**
   * Variables first, saved texts after them. Both are "something other than typed
   * words" and the search covers the lot, but a variable is the commoner answer and a
   * catalog of dozens of translations would otherwise push it below the fold.
   */
  const options = useMemo(
    () => [
      ...buildVariableOptions(variableGroups),
      ...buildTranslationOptions(translations),
    ],
    [variableGroups, translations],
  );
  const suggestions = useMemo(
    () => (trigger ? filterVariableOptions(options, trigger.query) : []),
    [options, trigger],
  );
  const open = enabled && !!trigger && suggestions.length > 0;

  // A narrowed query is a different shortlist; keeping the old row highlighted
  // would insert whatever happened to shift into that position.
  useEffect(() => setActiveIndex(0), [trigger?.query]);

  const sync = useCallback(
    (textBeforeCaret: string, baseOffset = 0) => {
      if (!enabled) return;
      const found = findVariableTrigger(
        textBeforeCaret,
        textBeforeCaret.length,
      );
      const next = found && {
        start: found.start + baseOffset,
        end: found.end + baseOffset,
        query: found.query,
      };

      if (next && dismissedRef.current === `${next.start}:${next.query}`) {
        setTrigger(null);
        return;
      }
      dismissedRef.current = null;

      setTrigger((current) => {
        if (!next || !current) return next || null;
        // Same trigger, new object: keep the old one rather than re-render the
        // list on every keystroke that did not change it.
        return next.start === current.start &&
          next.end === current.end &&
          next.query === current.query
          ? current
          : next;
      });
    },
    [enabled],
  );

  const close = useCallback(() => setTrigger(null), []);

  const accept = useCallback(
    (option: VariableOption) => {
      if (!trigger || !option) return;
      onAccept(option, trigger);
      setTrigger(null);
    },
    [trigger, onAccept],
  );

  const handleKey = useCallback(
    (key: string) => {
      if (!open) return false;

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        const step = key === 'ArrowDown' ? 1 : suggestions.length - 1;
        setActiveIndex((index) => (index + step) % suggestions.length);
        return true;
      }
      if (key === 'Enter' || key === 'Tab') {
        accept(suggestions[activeIndex] ?? suggestions[0]);
        return true;
      }
      if (key === 'Escape') {
        if (trigger) dismissedRef.current = `${trigger.start}:${trigger.query}`;
        setTrigger(null);
        return true;
      }
      return false;
    },
    [open, suggestions, activeIndex, accept, trigger],
  );

  return {
    options,
    suggestions,
    open,
    activeIndex,
    trigger,
    sync,
    handleKey,
    accept,
    close,
  };
}
